// perSf.js — the per-square-foot / per-unit figures on Parcel Details.
//
//   Land/LSF = MARKET Land  ÷ land area (sq ft)
//   Imp/SF   = MARKET Imp   ÷ NLA
//   Val/SF   = MARKET Total ÷ NLA
//   Val/Unit = MARKET Total ÷ Units
//
// ⛔ MARKET, never Assessed or Taxable. Verified on DeKalb 15 246 04 128 (ratio 0.400): the displayed
// Val/SF 355.28 is 50,315,900 / 141,624 — the market total, not the assessed 20,126,360.
// ⛔ NLA, never GBA. DeKalb GBA is 177,106, NLA 141,624; Imp/SF 339.89 = 48,136,550 / 141,624.
// assessor_bldg_sqft IS GBA and must never be used as a divisor.
//
// ⛔ A MISSING DIVISOR YIELDS null → the caller renders "-". Never 0, never a substituted divisor. This is
// the whole reason the rule lives in a function instead of being inlined four times: "-" means "we don't
// know the area", while a 0 would assert a worthless building and a fallback divisor would assert a
// number nobody can reproduce. SigerTax shows "-" here and so do we.
export const perSf = (numerator, divisor) => {
  const n = Number(numerator);
  const d = Number(divisor);
  if (numerator == null || divisor == null) return null;
  if (!Number.isFinite(n) || !Number.isFinite(d)) return null;
  if (d === 0) return null;
  const v = n / d;
  return Number.isFinite(v) ? v : null;
};

// Display: 2dp with thousands separators, or "-" when the figure could not be computed.
export const fmtPerSf = (v) => (v == null ? '-' : v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));

// Market Total for a value row. ⛔ DERIVED, NOT READ. parcel_appeals.market_total is a stored column that
// is only ever written by recomputeTotals() on a USER EDIT — nothing populates it on load, on import or
// on scan — so a row can carry Market Land and Market Imp and still have a NULL total, which rendered as
// "—" and blanked Val/SF and Val/Unit downstream. Summing at read time makes the displayed total agree
// with the components above it no matter how the row was created.
//
// Falls back to the stored value when there are no components: a total-only jurisdiction (the FL school
// column, whose TRIM notice gives a total with no land/imp split) keeps the figure it was given.
export const marketTotalOf = (row) => {
  const n = (v) => { const x = Number(v); return Number.isFinite(x) ? x : 0; };
  const sum = n(row?.market_land) + n(row?.market_imp) + n(row?.market_pp);
  if (sum) return sum;
  const stored = Number(row?.market_total);
  return Number.isFinite(stored) && stored !== 0 ? stored : null;
};

// The four figures for ONE jurisdiction row, override-aware.
// `row` is a parcel_appeals row: market_land / market_imp / market_total plus the *_override columns.
// Returns [{ key, label, value, overridden }] so the renderer stays dumb.
export const figuresFor = (row, { landAreaSqft, nlaSqft, units }) => {
  const pick = (override, computed) => (override == null || override === ''
    ? { value: computed, overridden: false }
    : { value: Number(override), overridden: true });
  const mktTotal = marketTotalOf(row);   // derived — see marketTotalOf; the stored column is often NULL
  return [
    { key: 'land_lsf', label: 'Land/LSF', ...pick(row?.land_lsf_override, perSf(row?.market_land,  landAreaSqft)) },
    { key: 'imp_sf',   label: 'Imp/SF',   ...pick(row?.imp_sf_override,   perSf(row?.market_imp,   nlaSqft)) },
    { key: 'val_sf',   label: 'Val/SF',   ...pick(row?.val_sf_override,   perSf(mktTotal, nlaSqft)) },
    { key: 'val_unit', label: 'Val/Unit', ...pick(row?.val_unit_override, perSf(mktTotal, units)) },
  ];
};
